import { useCallback, useMemo } from "react";
import {
  truncateItemsFamiliesText,
} from "./itemsFamiliesUi";

import { usePmsTheme } from "../../Components/PmsThemeContext";
import { itemCatalogText } from "./itemCatalogMessages";
import useItemDialogFocus from "./useItemDialogFocus";
import "../PurchasingUi.css";
import "./ItemCatalog.css";

const PREVIEW_LIMIT = 80;

export default function ItemsFamiliesImportPopup({
  isOpen,
  onClose,
  title,
  subtitle,
  fileName,
  rows,
  columns,
  error = "",
  isSubmitting = false,
  confirmLabel = "Confirm Import",
  onConfirm,
}) {
  const { themeId, languageId } = usePmsTheme();
  const t = useCallback((text) => itemCatalogText(languageId, text), [languageId]);
  const modalRef = useItemDialogFocus(isOpen, isSubmitting, onClose);
  const previewColumns = useMemo(() => {
    if (Array.isArray(columns) && columns.length) {
      return columns.map((column) =>
        typeof column === "string" ? { key: column, label: column } : column
      );
    }

    return Object.keys(rows?.[0] || {}).map((key) => ({
      key,
      label: key,
    }));
  }, [columns, rows]);

  const previewRows = useMemo(() => (rows || []).slice(0, PREVIEW_LIMIT), [rows]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  return (
    <div
      className="pilcore-purchasing-overlay pu-item-dialog spx-modalBackdrop"
      data-theme={themeId}
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <div ref={modalRef} tabIndex={-1} className="spx-modal spx-modal--wide" role="dialog" aria-modal="true" aria-label={t(title)}>
        <div className="spx-modalHeader">
          <div>
            <div className="spx-modalKicker">{t("Catalog Import")}</div>
            <h3 className="spx-modalTitle">{t(title)}</h3>
            <p className="spx-modalSubtitle">{t(subtitle)}</p>
          </div>

          <button
            type="button"
            className="spx-closeBtn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label={t("Close import preview")}
          >
            ×
          </button>
        </div>

        <div className="spx-modalBody">
          <div className="spx-previewMeta">
            <div className="spx-stat">
              <div className="spx-statLabel">{t("File")}</div>
              <div className="spx-statValue" title={fileName || "-"}>
                {truncateItemsFamiliesText(fileName || "-", 28)}
              </div>
            </div>
            <div className="spx-stat">
              <div className="spx-statLabel">{t("Rows")}</div>
              <div className="spx-statValue">{rows?.length || 0}</div>
            </div>
            <div className="spx-stat">
              <div className="spx-statLabel">{t("Columns")}</div>
              <div className="spx-statValue">{previewColumns.length}</div>
            </div>
          </div>

          <div className="ifm-previewNote">
            {t("Review the mapped rows before confirming.")} {t("Preview")}: <strong>{previewRows.length}</strong> {t("of")} <strong>{rows?.length || 0}</strong>.
          </div>

          <div className="spx-previewWrap">
            {!previewColumns.length || !previewRows.length ? (
              <div className="spx-empty">{t("There are no rows ready for import in this file.")}</div>
            ) : (
              <table className="spx-previewTable">
                <thead>
                  <tr>
                    {previewColumns.map((column) => (
                      <th key={column.key}>{t(column.label)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, index) => (
                    <tr key={`preview-row-${index}`}>
                      {previewColumns.map((column) => {
                        const rawValue =
                          typeof column.render === "function"
                            ? column.render(row)
                            : row?.[column.key];
                        const textValue =
                          rawValue === null || rawValue === undefined || rawValue === ""
                            ? "-"
                            : String(rawValue);

                        return (
                          <td key={`${index}-${column.key}`} title={textValue}>
                            {truncateItemsFamiliesText(textValue, 72)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {error ? <div className="spx-alert" role="alert">{t(error)}</div> : null}

          <div className="spx-modalFooter">
            <button type="button" className="spx-btn spx-btn--ghost" onClick={handleClose} disabled={isSubmitting}>
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="spx-btn spx-btn--success"
              onClick={onConfirm}
              disabled={isSubmitting || !rows?.length}
            >
              {t(isSubmitting ? "Importing..." : confirmLabel)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
